import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { applyPilot, pilotStats } from "../lib/api";
import {
  Sparkles,
  Heart,
  CheckCircle2,
  Loader2,
  Users,
  Gift,
} from "lucide-react";
import { toast } from "sonner";
import useSeo from "../lib/useSeo";

const STRUGGLES = [
  "Selective eating",
  "Mealtime tantrums",
  "Cooking two meals (kid + adult)",
  "Screens at the table",
  "Limited time / busy weeknights",
  "Repeating the same 5 meals",
  "Other",
];

export default function Pilot() {
  const [form, setForm] = useState({
    parent_name: "",
    email: "",
    city: "",
    state: "",
    kids_ages: "",
    biggest_struggle: STRUGGLES[0],
    cooking_habits: "",
    why_fit: "",
    instagram: "",
    // Honeypot — must remain empty. Bots fill every field; humans never see this.
    website: "",
  });
  const [stats, setStats] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [formMountedAt] = useState(() => Date.now());

  useSeo({
    title: "Become a Founding Family — Plate Buddies pilot program",
    description:
      "Apply to be one of 25 Founding Families. Get a free Premium Kit + your name in the launch credits in exchange for honest feedback over 4 weeks.",
    url: "https://platebuddies.com/pilot",
  });

  useEffect(() => {
    pilotStats().then(setStats).catch((err) => console.warn("[fetch] failed:", err));
    window.scrollTo(0, 0);
  }, []);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    // Honeypot: any bot that auto-fills every field gets silently rejected.
    if (form.website && form.website.trim() !== "") {
      // Pretend success to avoid tipping off scrapers.
      setSuccess({ ok: true, message: "Application received!" });
      return;
    }
    // Time check: humans take more than 2 seconds to fill out a form.
    if (Date.now() - formMountedAt < 2000) {
      setSuccess({ ok: true, message: "Application received!" });
      return;
    }
    for (const k of ["parent_name", "email", "city", "kids_ages", "cooking_habits", "why_fit"]) {
      if (!form[k]?.trim()) {
        toast.error("Please fill out all required fields.");
        return;
      }
    }
    setSubmitting(true);
    try {
      // Strip honeypot before sending to backend.
      const { website, ...payload } = form;  // eslint-disable-line no-unused-vars
      const data = await applyPilot(payload);
      setSuccess(data);
      toast.success("Application received!");
      pilotStats().then(setStats).catch((err) => console.warn("[fetch] failed:", err));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't submit. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-pilot" className="font-body bg-pb-cream min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="blob bg-pb-sun" style={{ width: 420, height: 420, top: -100, left: -100, opacity: 0.45 }} />
        <div className="blob bg-pb-coral" style={{ width: 360, height: 360, top: 40, right: -100, opacity: 0.3 }} />
        <div className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-14 pb-10 text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/80 backdrop-blur px-4 py-1.5 text-xs font-extrabold uppercase tracking-widest text-pb-coral border border-pb-coral/20">
            <Sparkles className="h-3.5 w-3.5" /> 25 Founding Families · Selective
          </span>
          <h1 className="font-display font-black tracking-tight text-pb-ink mt-5 text-5xl md:text-6xl leading-[0.95]">
            Become a <br />
            <span className="wavy">Founding Family.</span>
          </h1>
          <p className="mt-5 max-w-2xl mx-auto text-lg text-pb-soft">
            We're picking <strong>25 thoughtful families</strong> to co-design Plate Buddies with us. You get the full <strong>Premium Kit free</strong> — you just cover $12 shipping — in exchange for honest feedback over 4 weeks.
          </p>

          {/* Spots remaining */}
          {stats && (
            <div className="mt-6 inline-flex items-center gap-3 bg-white rounded-full px-5 py-2.5 shadow-md border border-pb-sky/30">
              <Users className="h-4 w-4 text-pb-coral" />
              <span className="font-extrabold text-sm">
                {stats.spots_filled}/{stats.spots_total} founding families joined · {stats.spots_remaining} spots left
              </span>
            </div>
          )}
        </div>
      </section>

      {/* Perks */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-10">
        <div className="grid sm:grid-cols-3 gap-5">
          <Perk
            icon={Gift}
            color="#FFD54F"
            bg="#FFF6D6"
            title="Free Premium Kit"
            text="$49.99 value. You only pay $12 shipping."
          />
          <Perk
            icon={Heart}
            color="#F26A34"
            bg="#FFE3D8"
            title="Co-design credit"
            text="Your name in the launch credits + lifetime discount on add-ons."
          />
          <Perk
            icon={Sparkles}
            color="#4DD0E1"
            bg="#D6F4F8"
            title="Early access"
            text="First look at new Buddies, kits, and mealtime tools before anyone else."
          />
        </div>
      </section>

      {/* Form */}
      <section id="apply" className="max-w-3xl mx-auto px-5 sm:px-8 pb-24">
        {success ? (
          <div data-testid="pilot-success" className="bg-white rounded-[2.5rem] p-10 sm:p-14 shadow-[0_24px_60px_rgba(28,22,40,0.08)] text-center">
            <CheckCircle2 className="h-14 w-14 text-pb-green mx-auto" />
            <h2 className="font-display font-black text-3xl mt-5">Application received!</h2>
            <p className="text-pb-soft mt-3 max-w-md mx-auto">
              You're #{success.position} in line. We'll review and reach out within a few days. If selected, we'll email you a private link with your <strong>PILOT</strong> code to claim the Premium Kit for $12 shipping.
            </p>
            <div className="mt-6 flex flex-wrap gap-3 justify-center">
              <Link to="/home" className="rounded-full bg-pb-ink text-white font-bold px-6 py-3 hover:bg-pb-coral transition">
                Back home
              </Link>
              <Link to="/planner" className="rounded-full bg-pb-cream text-pb-ink font-bold px-6 py-3 border-2 border-pb-sky/40 hover:border-pb-coral transition">
                Try the meal planner
              </Link>
            </div>
          </div>
        ) : (
          <form
            data-testid="pilot-form"
            onSubmit={onSubmit}
            className="bg-white rounded-[2.5rem] p-8 sm:p-12 shadow-[0_24px_60px_rgba(28,22,40,0.08)] space-y-6"
          >
            <div>
              <p className="text-sm font-extrabold uppercase tracking-widest text-pb-coral">Tell us about your family</p>
              <h2 className="font-display font-black text-3xl mt-2 leading-tight">Apply to be a Founding Family</h2>
              <p className="text-pb-soft text-sm mt-1">~3 minutes. We read every application personally.</p>
            </div>

            {/* Honeypot — invisible to humans, irresistible to bots. Do not remove. */}
            <div aria-hidden="true" style={{ position: "absolute", left: "-10000px", top: "auto", width: "1px", height: "1px", overflow: "hidden" }}>
              <label htmlFor="pb-website-field">Website (leave blank)</label>
              <input
                id="pb-website-field"
                type="text"
                name="website"
                tabIndex={-1}
                autoComplete="off"
                value={form.website}
                onChange={setField("website")}
              />
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="Parent's name *" testid="pilot-name" value={form.parent_name} onChange={setField("parent_name")} />
              <TextField label="Email *" testid="pilot-email" type="email" value={form.email} onChange={setField("email")} />
              <TextField label="City *" testid="pilot-city" value={form.city} onChange={setField("city")} />
              <TextField label="State / Region" testid="pilot-state" value={form.state} onChange={setField("state")} />
            </div>

            <TextField
              label="Your kids' ages * (e.g. 3 & 6)"
              testid="pilot-ages"
              value={form.kids_ages}
              onChange={setField("kids_ages")}
            />

            <SelectField label="Biggest mealtime struggle *" testid="pilot-struggle" value={form.biggest_struggle} onChange={setField("biggest_struggle")} options={STRUGGLES} />

            <TextArea
              label="What does cooking + eating together look like in your house? *"
              testid="pilot-habits"
              value={form.cooking_habits}
              onChange={setField("cooking_habits")}
              placeholder="A snapshot of a typical week — what works, what doesn't."
            />

            <TextArea
              label="Why would you be a great pilot family? *"
              testid="pilot-fit"
              value={form.why_fit}
              onChange={setField("why_fit")}
              placeholder="Honest, thoughtful, willing to share feedback — tell us what makes you a fit."
            />

            <TextField
              label="Instagram handle (optional)"
              testid="pilot-instagram"
              value={form.instagram}
              onChange={setField("instagram")}
              placeholder="@yourhandle"
            />

            <button
              data-testid="btn-submit-pilot"
              type="submit"
              disabled={submitting}
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-pb-coral text-white font-extrabold px-8 py-4 shadow-[0_12px_30px_rgba(242,106,52,0.4)] hover:scale-[1.02] transition disabled:opacity-60"
            >
              {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              {submitting ? "Sending…" : "Apply for the pilot"}
            </button>
            <p className="text-xs text-pb-soft text-center">
              We'll only use this to evaluate the pilot. No spam, ever.
            </p>
          </form>
        )}
      </section>

      <Footer />
    </div>
  );
}

function Perk({ icon: Icon, color, bg, title, text }) {
  return (
    <div className="rounded-3xl p-6 border border-white shadow-[0_14px_32px_rgba(28,22,40,0.06)]" style={{ background: bg }}>
      <span className="h-12 w-12 rounded-2xl flex items-center justify-center bg-white shadow-sm" style={{ color }}>
        <Icon className="h-5 w-5" />
      </span>
      <h3 className="font-display font-black text-xl mt-4">{title}</h3>
      <p className="text-sm text-pb-ink/70 mt-1">{text}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs font-extrabold uppercase tracking-widest text-pb-soft">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function TextField({ label, value, onChange, testid, type = "text", placeholder }) {
  return (
    <Field label={label}>
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className="w-full rounded-2xl border-2 border-pb-sky/40 bg-pb-cream px-4 py-3 font-bold text-pb-ink placeholder:text-pb-soft/60 focus:outline-none focus:border-pb-coral focus:bg-white transition"
      />
    </Field>
  );
}

function TextArea({ label, value, onChange, testid, placeholder }) {
  return (
    <Field label={label}>
      <textarea
        data-testid={testid}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={4}
        className="w-full rounded-2xl border-2 border-pb-sky/40 bg-pb-cream px-4 py-3 font-bold text-pb-ink placeholder:text-pb-soft/60 focus:outline-none focus:border-pb-coral focus:bg-white transition resize-none leading-snug"
      />
    </Field>
  );
}

function SelectField({ label, value, onChange, testid, options }) {
  return (
    <Field label={label}>
      <select
        data-testid={testid}
        value={value}
        onChange={onChange}
        className="w-full rounded-2xl border-2 border-pb-sky/40 bg-pb-cream px-4 py-3 font-bold text-pb-ink focus:outline-none focus:border-pb-coral focus:bg-white transition appearance-none"
      >
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    </Field>
  );
}
